import { Directive, inject, Input, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslocoService } from '@jsverse/transloco';
import { PageTitleService } from '@modules/dashboard/main-layout/page-title.service';
import { WA_INTERSECTION_THRESHOLD, WaIntersectionObserverService } from '@ng-web-apis/intersection-observer';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map, startWith } from 'rxjs';

const PREVENT_BLINK_DELAY = 600;

@UntilDestroy()
@Directive({
  selector: '[scrollHeaderTitle]',
  standalone: true,
  providers: [
    WaIntersectionObserverService,
    {
      // https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/IntersectionObserver#threshold
      provide: WA_INTERSECTION_THRESHOLD,
      useValue: 0.8,
    },
  ],
})
export class ScrollHeaderTitleDirective implements OnDestroy {
  private readonly browserTitle = inject(Title);
  private readonly pageTitle = inject(PageTitleService);
  private readonly intersectionObserverService = inject(WaIntersectionObserverService);
  private readonly translocoService = inject(TranslocoService);

  private readonly appName = this.translocoService.translate('Pixacare');

  @Input() set scrollHeaderTitle(value: string) {
    setTimeout(() => {
      this.pageTitle.setTitle(value);
    }, PREVENT_BLINK_DELAY);
    this.browserTitle.setTitle(`${value} - ${this.appName}`);

    this.intersectionObserverService
      .pipe(
        untilDestroyed(this),
        map(([entry]) => entry.isIntersecting),
        startWith(true),
      )
      .subscribe((visible) => {
        this.pageTitle.updateVisible(!visible);
      });
  }

  @Input() set scrollHeaderBackLink(value: string) {
    setTimeout(() => {
      this.pageTitle.setBackLink(value);
    }, PREVENT_BLINK_DELAY);
  }

  ngOnDestroy(): void {
    this.pageTitle.updateVisible(false);
    this.pageTitle.setBackLink(null);
    this.pageTitle.setTitle('');
    this.browserTitle.setTitle(`${this.appName}`);
  }
}
